import * as React from "react";
import * as SheetPrimitive from "@radix-ui/react-dialog";
import { cva, type VariantProps } from "class-variance-authority";
import { X } from "lucide-react";
import { cn } from "@/shared/lib/cn";

const Sheet = SheetPrimitive.Root;

const SheetOverlay = React.forwardRef<
    React.ElementRef<typeof SheetPrimitive.Overlay>,
    React.ComponentPropsWithoutRef<typeof SheetPrimitive.Overlay>
>(({ className, ...props }, ref) => (
    <SheetPrimitive.Overlay
        ref={ref}
        className={cn(
            "fixed inset-0 z-drawer_overlay bg-black/50 backdrop-blur-sm data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=open]:fade-in-0 data-[state=closed]:fade-out-0",
            className
        )}
        {...props}
    />
));
SheetOverlay.displayName = SheetPrimitive.Overlay.displayName;

const sheetVariants = cva(
    "fixed z-drawer flex flex-col gap-4 bg-white dark:bg-night_surface shadow-lg outline-none transition ease-in-out data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=open]:duration-300 data-[state=closed]:duration-200",
    {
        variants: {
            side: {
                top: "inset-x-0 top-0 border-b border-slate-200 dark:border-white/10 data-[state=open]:slide-in-from-top data-[state=closed]:slide-out-to-top",
                bottom: "inset-x-0 bottom-0 max-h-[92dvh] rounded-t-2xl border-t border-slate-200 dark:border-white/10 pb-[env(safe-area-inset-bottom)] data-[state=open]:slide-in-from-bottom data-[state=closed]:slide-out-to-bottom",
                left: "inset-y-0 left-0 h-full w-[85%] max-w-sm pt-[env(safe-area-inset-top)] border-r border-slate-200 dark:border-white/10 data-[state=open]:slide-in-from-left data-[state=closed]:slide-out-to-left",
                right: "inset-y-0 right-0 h-full w-[85%] max-w-sm pt-[env(safe-area-inset-top)] border-l border-slate-200 dark:border-white/10 data-[state=open]:slide-in-from-right data-[state=closed]:slide-out-to-right",
            },
        },
        defaultVariants: {
            side: "right",
        },
    }
);

interface SheetContentProps
    extends React.ComponentPropsWithoutRef<typeof SheetPrimitive.Content>,
        VariantProps<typeof sheetVariants> {
    hideClose?: boolean;
}

const SheetContent = React.forwardRef<React.ElementRef<typeof SheetPrimitive.Content>, SheetContentProps>(
    ({ side = "right", hideClose = false, className, children, ...props }, ref) => (
        <SheetPrimitive.Portal>
            <SheetOverlay />
            <SheetPrimitive.Content ref={ref} className={cn(sheetVariants({ side }), className)} {...props}>
                {children}
                {!hideClose && (
                    <SheetPrimitive.Close
                        aria-label="Fechar"
                        className="absolute right-2 top-2 rounded-lg p-3 md:right-3 md:top-3 md:p-2 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-dark_green/40 dark:text-zinc-500 dark:hover:bg-night_input dark:hover:text-zinc-200"
                    >
                        <X size={20} />
                    </SheetPrimitive.Close>
                )}
            </SheetPrimitive.Content>
        </SheetPrimitive.Portal>
    )
);
SheetContent.displayName = SheetPrimitive.Content.displayName;

const SheetTitle = React.forwardRef<
    React.ElementRef<typeof SheetPrimitive.Title>,
    React.ComponentPropsWithoutRef<typeof SheetPrimitive.Title>
>(({ className, ...props }, ref) => (
    <SheetPrimitive.Title
        ref={ref}
        className={cn("text-[17px] font-semibold tracking-tight text-slate-800 dark:text-zinc-100", className)}
        {...props}
    />
));
SheetTitle.displayName = SheetPrimitive.Title.displayName;

export { Sheet, SheetContent, SheetTitle };
